import { ActionOption, ButtonWithActionMenu } from './atoms/ButtonWithActionMenu';
import { SalesforceItem } from '../../../parser/ConfigurationItemTypes';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import Stack from '@mui/material/Stack';
import { useState, useCallback } from 'react';
import { Button } from '@sweep-io/sweep-design';
import { useDocumentationApiFacade } from '../../../../apis/facades/useDocumentationApiFacade';
import { telemetry } from '../../../../telemetry';
import { OBJECT } from '@server/parser';
import { DocumentationCardHeaderLayout } from '../../building-blocks/documentation-card/DocumentationCardHeaderLayout';
import { SfConfigTitle } from './atoms/SfConfigTitle';

const downloadAsJson = (content: string, fileName: string) => {
  const jsonContent = JSON.stringify({ fileContent: content }, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface DependenciesHeaderProps {
  configurationItem: SalesforceItem;
  type: string;
  crmOrgId: string;
  shouldDisplayBackButton?: boolean;
  isReadOnly?: boolean;
  isOrgMigrationRoute?: boolean;
  showAskAiButton?: boolean;
  onAskAiClick?: () => void;
  onGoBack?: () => void;
  saltoObjectTypes?: Pick<ObjectTypeName, 'objectType' | 'label'>[];
}

export const DependenciesHeader = ({
  configurationItem,
  type,
  crmOrgId,
  shouldDisplayBackButton = false,
  isReadOnly = false,
  isOrgMigrationRoute = false,
  showAskAiButton = false,
  onAskAiClick,
  onGoBack,
  saltoObjectTypes,
}: DependenciesHeaderProps) => {
  const { searchInDocumentationCard } = useFeatureToggle();
  const { updatedBy, updatedAt, metadataId, name } = configurationItem ?? {};
  const { get_NaclFile } = useDocumentationApiFacade();
  const [isDownloading, setIsDownloading] = useState(false);

  const isObjectType = type === OBJECT;
  const showBuildAgentButton = isOrgMigrationRoute && !isObjectType;

  const handleSendToBuildAgent = useCallback(async () => {
    if (!metadataId) return;

    setIsDownloading(true);
    try {
      const naclContent = await get_NaclFile({ crmOrgId, metadataId });
      const fileName = `${name || 'configuration'}.json`;
      downloadAsJson(naclContent, fileName);
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      setIsDownloading(false);
    }
  }, [crmOrgId, metadataId, name, get_NaclFile]);

  const sfActions = showBuildAgentButton ? (
    <Stack sx={{ '& button': { whiteSpace: 'nowrap' } }}>
      <Button
        variant="flat-grey"
        size="small"
        startIconName="Upload"
        onClick={handleSendToBuildAgent}
        loading={isDownloading}
      >
        Send to Build Agent
      </Button>
    </Stack>
  ) : (
    <ButtonWithActionMenu
      lastUpdatedInfo={updatedBy && updatedAt ? { updatedBy, updatedAt } : undefined}
      configurationItem={configurationItem}
      crmOrgId={crmOrgId}
      options={[
        ActionOption.openInSalesforce,
        ActionOption.viewSource,
        ...(!isReadOnly && type !== OBJECT ? [ActionOption.comments] : []),
      ]}
    />
  );

  return (
    <DocumentationCardHeaderLayout
      title={
        <SfConfigTitle
          configurationItem={configurationItem}
          type={type}
          saltoObjectTypes={saltoObjectTypes ?? []}
        />
      }
      onGoBack={onGoBack}
      shouldDisplayBackButton={shouldDisplayBackButton}
      searchEnabled={searchInDocumentationCard && !showBuildAgentButton}
      showAskAiButton={showAskAiButton && !showBuildAgentButton}
      onAskAiClick={onAskAiClick}
      actions={sfActions}
    />
  );
};
